import { useFocusOnMount } from "../../../../../../shared/hooks/useFocusOnMount";
import { usePageTitle } from "../../../../../../shared/hooks/usePageTitle";
import { SUPPORT_HREF, publicPagePath, supportHrefWithContext } from "../../../lib/support";
import { lastSupportEventId } from "../../../../../../shared/errorReporting";
import { useState, useSyncExternalStore, type MouseEvent, type ReactNode, type Ref } from "react";
import { useTranslation } from "../../../i18n/useTranslation";
import { StepLeadBar, StepMeta } from "../StepChrome";
import { FlowFrame, arrivedByHistory } from "../../../components/PageShell";



/**
 * The guest-verification screens. Presentational: each receives the checkout
 * state and callbacks, and none of them sends anything on its own.
 *
 * Layout is the booking flow's own (`.b-app`, `.b-scroll`, `.b-foot`), so the
 * web v2 kit retune applies without a second vocabulary. Each screen moves
 * focus to its heading on arrival, so a screen reader hears the new page from
 * its own name, and each status line has the live role its meaning calls for:
 * `alert` for something that stopped the person, `status` for everything else.
 */

export function Screen({
  testId,
  heading,
  sub,
  children,
  foot,
  focusKey,
  describedBy,
  codeLayout = false,
  secondary,
  outcome,
}: {
  testId: string;
  heading: string;
  /**
   * Set on the screens that are a booking OUTCOME (a booking may exist):
   * they are titled "Booking details · <business> · Lura" like every outcome
   * (views/Book/outcomeTitle.tsx), never by their heading. Every other
   * verification step is titled by its visible heading.
   */
  outcome?: { business: string };
  sub?: ReactNode;
  children?: ReactNode;
  foot?: ReactNode;
  /**
   * Changes when the control the person was using is REPLACED on the same
   * screen. Focus returns to the heading rather than falling to <body>
   * (QE A2-2).
   */
  focusKey?: string;
  /**
   * The id of the text that explains this screen. Focus lands on the heading,
   * and a live region that is already filled when it appears is usually not
   * announced, so the heading carries the explanation as its description
   * (QE P2-3, 2026-09-23).
   */
  describedBy?: string;
  codeLayout?: boolean;
  secondary?: ReactNode;
}) {
  // Arrival focus is for a user's step change (c6-3): a screen that Back or
  // Forward re-mounted keeps focus where it is (rc.13). A change of
  // `focusKey` later on the same screen is the person's own action and still
  // moves focus, including a change back to the key it mounted with: the
  // arrival's exemption ends at the first change (review of rc.13, P3).
  const screenKey = `${testId}|${focusKey ?? ""}`;
  const [historyArrival, setHistoryArrival] = useState(() => arrivedByHistory());
  const [mountKey] = useState(screenKey);
  if (historyArrival && screenKey !== mountKey) setHistoryArrival(false);
  const ref = useFocusOnMount({ on: [testId, focusKey], when: !historyArrival || screenKey !== mountKey });
  const { t } = useTranslation();
  usePageTitle(outcome ? t("flow.outcome.pageTitle") : heading, outcome?.business);
  return (
    // A flow frame (c6-2): inside Book's shell, through GuestFlowHost's
    // portal, it publishes its lead and test id and draws only its body.
    <FlowFrame testId={testId} className={codeLayout ? "b3-code" : undefined} lead={<StepLeadBar step={5} of={5} v3 label={heading} />}>
      <div className="b-scroll">
        <div className="b-stack g6">
          <h1 className="b-display" tabIndex={-1} ref={ref} aria-describedby={describedBy}>
            {heading}
          </h1>
          {sub ? <p className="b-p">{sub}</p> : null}
        </div>
        {children}
        {secondary ? <div className="b3-secondary-actions">{secondary}</div> : null}
        <StepMeta />
      </div>
      {foot ? <div className="b-foot">{foot}</div> : null}
    </FlowFrame>
  );
}

export function Line({ tone, role, children, testId, id, lineRef }: { tone: "danger" | "warning" | "quiet" | "info"; role?: "alert" | "status"; children: ReactNode; testId?: string; id?: string; lineRef?: Ref<HTMLDivElement> }) {
  return (
    <div ref={lineRef} id={id} className={`b-notice t-${tone}`} role={role} aria-live={role === "status" ? "polite" : undefined} data-testid={testId}>
      <div className="b-notice-b">
        <p className="b-p">{children}</p>
      </div>
    </div>
  );
}

/** The browser's connectivity, for the offline note only. Sending is refused in the hook either way. */
function subscribeOnline(onChange: () => void) {
  window.addEventListener("online", onChange);
  window.addEventListener("offline", onChange);
  return () => {
    window.removeEventListener("online", onChange);
    window.removeEventListener("offline", onChange);
  };
}
function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
}

/**
 * OFFLINE, SAID OUT LOUD. The hook refuses to send while offline, and a
 * control that does nothing with no explanation reads as a broken page. This
 * says what is true: nothing goes, and nothing is waiting to go.
 */
export function OfflineNote() {
  const { t } = useTranslation();
  const online = useOnline();
  if (online) return null;
  return (
    <Line tone="warning" role="status" testId="guest-offline">
      {t("guest.offline")}
    </Line>
  );
}

/**
 * "Get help". With `context`, the email it opens carries PUBLIC facts the
 * page already shows or knows (what happened, the business, the page path,
 * the time, the build, an error reference), so support can act without asking.
 * Built at click time, not render: the time is current and the render stays
 * pure. Without JavaScript the bare `mailto:` stands. Nothing personal can be
 * passed in: the context type has no field for it (lib/support.ts).
 */
export function HelpLink({ context }: { context?: { heading: string; business: string } }) {
  const { t } = useTranslation();
  const onClick = context
    ? (event: MouseEvent<HTMLAnchorElement>) => {
        const now = new Date();
        const page = publicPagePath(window.location.pathname);
        const reference = lastSupportEventId();
        const build = typeof __LURA_BUILD_COMMIT__ === "string" ? __LURA_BUILD_COMMIT__.slice(0, 7) : "";
        event.currentTarget.href = supportHrefWithContext({
          subject: t("guest.help.mail.subject"),
          lines: [
            t("guest.help.mail.intro"),
            "",
            t("guest.help.mail.happened", { heading: context.heading }),
            t("guest.help.mail.business", { business: context.business }),
            page ? t("guest.help.mail.page", { page }) : null,
            t("guest.help.mail.time", {
              time: now.toLocaleString(),
              zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
            build ? t("guest.help.mail.build", { build }) : null,
            reference ? t("guest.help.mail.reference", { reference }) : null,
          ],
        });
      }
    : undefined;
  return (
    <a className="b-btn v-secondary is-full" href={SUPPORT_HREF} data-testid="guest-help" onClick={onClick}>
      {t("guest.help")}
    </a>
  );
}

/** Deadline and zone for the "Finish booking by" line. */
export interface HoldView {
  expiresAt: string | null;
  timeZone: string;
  /** What is being booked, formatted by the flow. See FinishBy. */
  summary?: string;
}

/** Instants formatted for copy: always the professional's timezone. */
export function useAt(timeZone: string) {
  const { fmt } = useTranslation();
  return (ms: number) => fmt.time(new Date(ms).toISOString(), timeZone);
}
